import { JsonObject, JsonValue, MonitorNodeChange, MonitorRunDetail, MonitorRunSummary } from './types';

export type SemanticTone = 'default' | 'primary' | 'success' | 'warning' | 'error';

export const statusTone = (status: string): SemanticTone => {
    if (status === 'succeeded' || status === 'completed') return 'success';
    if (status === 'failed') return 'error';
    if (status === 'partial' || status === 'warning' || status === 'retry_wait' || status === 'blocked')
        return 'warning';
    if (
        status === 'queued' ||
        status === 'running' ||
        status === 'working' ||
        status === 'planning' ||
        status === 'leased' ||
        status === 'cancelling' ||
        status === 'waiting_for_model'
    )
        return 'primary';
    return 'default';
};

export const humanize = (value: unknown): string =>
    String(value ?? '')
        .replaceAll('_', ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());

export const isOpaqueIdentifier = (value: unknown): boolean => {
    const text = String(value ?? '').trim();
    if (text === '') return false;
    return (
        /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(text) ||
        /^(?:model|work|run|snapshot|candidate|revision|artifact)_[0-9a-z-]{12,}$/i.test(text) ||
        /^[0-9a-f]{32,}$/i.test(text)
    );
};

export const isTechnicalIdentifierField = (key: string, value: unknown): boolean => {
    if (key === 'run_uid') return false;
    return (
        /(^|_)(?:uid|id)$/.test(key) ||
        /(?:^|_)(?:hash|identity|identity_key|token|locator)$/.test(key) ||
        ['dedupe_key', 'lease_owner'].includes(key) ||
        isOpaqueIdentifier(value)
    );
};

export const safeDisplayName = (value: unknown, fallback: string): string => {
    const text = String(value ?? '').trim();
    return text !== '' && !isOpaqueIdentifier(text) ? text : fallback;
};

export const profileValuesDisplay = (values: JsonValue | undefined, minValue: JsonValue | undefined): string =>
    Array.isArray(values) ? values.slice(0, 6).join(', ') : String(values ?? minValue ?? '—');

export const jsonString = (value: JsonValue | undefined): string | undefined =>
    typeof value === 'string' ? value : undefined;

export const jsonKey = (value: JsonValue | undefined): string | number | undefined =>
    typeof value === 'string' || typeof value === 'number' ? value : undefined;

export const jsonNumber = (value: JsonValue | undefined, fallback = 0): number =>
    typeof value === 'number' ? value : fallback;

export const asJsonObject = (value: JsonValue | undefined): JsonObject | undefined =>
    value && typeof value === 'object' && !Array.isArray(value) ? value : undefined;

export const errorStatus = (error: unknown): number | undefined =>
    error && typeof error === 'object' ? (error as { response?: { status?: number } }).response?.status : undefined;

export const recordEntries = (record: JsonObject, technical: boolean) =>
    Object.entries(record).filter(
        ([key, value]) => value !== null && value !== '' && isTechnicalIdentifierField(key, value) === technical,
    );

export const statusLabel = (status: string): string => {
    const labels: Record<string, string> = {
        succeeded: 'Completed',
        warning: 'Completed with issues',
        not_run: 'Not run',
        partial: 'Completed with issues',
        leased: 'Working',
        retry_wait: 'Waiting to retry',
        blocked: 'Waiting for prerequisite',
        waiting_for_model: 'Waiting for model',
    };
    return labels[status] ?? humanize(status);
};

export const parseMonitorDate = (value?: string | null): Date | null => {
    if (!value) return null;

    const normalized = value.replace(' ', 'T');
    const timezoneAware = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
    const date = new Date(timezoneAware ? normalized : `${normalized}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
};

// Dates render in the server's time, never the viewer's (MI-30498). The API sends the server's wall clock with the
// server's offset, so a screen prints the digits as sent; durations keep the full instant (parseMonitorDate).
export const serverWallClockDigits = (value?: string | null): string | null => {
    if (!value) return null;
    return value
        .trim()
        .replace(' ', 'T')
        .replace(/(?:Z|[+-]\d{2}:?\d{2})$/i, '');
};

// A Date whose UTC fields are the server's digits: format it with timeZone 'UTC' or getUTC*.
export const serverWallClock = (value?: string | null): Date | null => {
    const digits = serverWallClockDigits(value);
    if (!digits) return null;
    const date = new Date(`${digits}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
};

// Formats the server's wall clock with fixed en-US parts; never parses the digits in the viewer's zone.
export const formatServerWallClock = (
    value: string | null | undefined,
    options: Intl.DateTimeFormatOptions,
): string => {
    const date = serverWallClock(value);
    return date ? date.toLocaleString('en-US', { ...options, timeZone: 'UTC' }).replace(/\u202f/g, ' ') : '';
};

// "UTC" or "UTC+03:00": the zone the server's ATOM stamps are written in.
export const serverZoneLabel = (value?: string | null): string => {
    const offset = value?.trim().match(/([+-]\d{2}:?\d{2})$/)?.[1];
    return !offset || /^[+-]00:?00$/.test(offset) ? 'UTC' : `UTC${offset}`;
};

// The one absolute-timestamp shape across Concierge screens (MI-30650): "10/5/2026, 3:39:08 PM", never the browser locale.
export const SERVER_DATE_TIME: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
};

export const formatDateTime = (value?: string | null): string => {
    if (!value) return '—';
    return formatServerWallClock(value, SERVER_DATE_TIME) || value;
};

export const formatDuration = (start?: string | null, finish?: string | null, nowMs = Date.now()): string => {
    if (!start) return '—';
    const startDate = parseMonitorDate(start);
    const finishDate = parseMonitorDate(finish);
    if (!startDate || (finish && !finishDate)) return '—';
    const seconds = Math.max(0, Math.floor(((finishDate?.getTime() ?? nowMs) - startDate.getTime()) / 1000));
    if (seconds === 0) return '<1s';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};

export const runOperationName = (
    run: Pick<MonitorRunSummary, 'resource_name' | 'engine'> & { models?: MonitorRunSummary['models'] },
): string =>
    run.resource_name ||
    (run.models ?? [])
        .map((model) => safeDisplayName(model.display_name, `${humanize(run.engine)} model`))
        .join(', ') ||
    humanize(run.engine);

// Every listed model gets a name (an engine-based one at worst), so an empty join means no model at
// all: the same placeholder the detail page shows, never the count (MI-30520: "0").
export const runModelNames = (run: MonitorRunSummary): string =>
    run.models.map((model) => safeDisplayName(model.display_name, `${humanize(run.engine)} model`)).join(', ') || '—';

export function changeKey(change: MonitorNodeChange, index: number): string {
    return `${change.object_type}-${change.object_uid || index}-${change.sequence_no ?? index}`;
}

export function formatChangeValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
}
